import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core'
import { Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import {
  AbstractComponent,
  Channel,
  Company,
  ConfirmationComponent,
  Department,
  DepartmentService,
  Team,
  TeamService,
  User
} from 'lib-trend-core'
import { DepartmentScheduleComponent } from '../department-schedule/department-schedule.component'
import { DepartmentTeamEditComponent } from '../department-team-edit/department-team-edit.component'

@Component({
    selector: 'department-edit-component',
    // templateUrl: './department-edit.component.html',
    templateUrl: './department-edit.v2.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DepartmentEditComponent extends AbstractComponent {
  departament: Department
  teamList: Team[] = [];
  teamsLoaded: boolean = false;
  indexExpanded: number = -1

  showTeam: boolean = false
  indexEdit: number

  channelId: string;

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    public departmentService: DepartmentService,
    public teamService: TeamService,
    public dialogRef: MatDialogRef<DepartmentEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Department,
  ) {
    super(injector)
    this.createForm()
  }

  ngOnInit() {
    const departamentId = this.data._id;
    this.channelId = this.data.channel._id;
    this.isNew = !departamentId;

    if (!departamentId) {
      this.teamsLoaded = true;
      return;
    }

    this.departmentService.getById(departamentId).subscribe((departament) => {
      this.departament = departament
      if (this.departament) {
        this.formGroup.patchValue({ ...this.departament })
      }
    })
    this.teamService.getByDepartment(departamentId).subscribe({
      next: (resp) => {
        this.teamList = resp ?? []
        this.teamsLoaded = true
      },
      error: (err) => {
        this.teamsLoaded = false
        this.alertService.error(err?.error?.message ?? this.t('department.edit.teamsLoadError'))
      },
    })
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      _id: [null],
      name: [null, Validators.required],
      description: [null, Validators.required],
    })
  }

  close(): void {
    this.dialogRef.close(true);
  }

  save() {
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup)
      return
    }

    if (!this.isNew && !this.teamsLoaded) {
      this.alertService.error(this.t('department.edit.teamsNotLoaded'))
      return
    }

    const department = this.createObject()
    this.loading = true

    if (!department._id) {
      this.departmentService
        .createDepartmentTeams(department, this.teamList)
        .subscribe({
          next: () => {
            this.loading = false
            this.alertService.success(this.t('department.edit.saveSuccess'))
            this.dialogRef.close(true);
          },
          error: (err) => {
            this.loading = false
            this.alertService.error(err?.error?.message ?? this.t('department.edit.saveError'))
          },
        })
    } else {
      this.departmentService
        .updateDepartmentTeams(department, this.teamList)
        .subscribe({
          next: () => {
            this.loading = false
            this.alertService.success(this.t('department.edit.updateSuccess'))
            this.dialogRef.close(true);
          },
          error: (err) => {
            this.loading = false
            this.alertService.error(err?.error?.message ?? this.t('department.edit.saveError'))
          },
        })
    }
  }

  operatingMembers(members: Array<User>): Array<User> {
    return (members ?? []).filter((member: User) => member?.blocked !== true && member?.removed !== true);
  }

  removeAgent(team: Team, agent: User) {
    this.removeMember(team?.agents, agent);
  }

  removeSupervisor(team: Team, supervisor: User) {
    this.removeMember(team?.supervisors, supervisor);
  }

  private removeMember(members: Array<User>, member: User): void {
    const index = (members ?? []).findIndex((current: User) => current?._id === member?._id);
    if (index > -1) {
      members.splice(index, 1);
    }
  }

  private createObject(): Department {
    const departament = {} as Department
    departament._id = this.formGroup.get('_id').value
    departament.name = this.formGroup.get('name').value
    departament.description = this.formGroup.get('description').value
    departament.company = {} as Company;
    departament.company._id = this.getIDCurrentCompany();
    departament.channel = {} as Channel;
    departament.channel._id = this.channelId;
    return departament
  }

  isCreate() {
    return !this.formGroup.get('id').value
  }

  activateInactivateTeam(team: Team, index: any) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px'
    })
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.teamList[index].active = !team.active
      }
    })
  }

  deleteTeam(index: any) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: this.t('department.edit.confirmRemoveTeam')
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.teamList.splice(index, 1)
      }
    })
  }

  editTeam(index: number) {
    this.indexEdit = index
    const team = structuredClone(this.teamList[index])
    const dialogRef = this.dialog.open(DepartmentTeamEditComponent, {
      width: '800px',
      data: team,
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.teamList[this.indexEdit] = result
      }
    })
  }

  getWeekDays(item: Team) {
    let weekDays = ''
    if (item.monday) {
      weekDays ? (weekDays += ' - Seg') : (weekDays += 'Seg')
    }
    if (item.tuesday) {
      weekDays ? (weekDays += ' - Ter') : (weekDays += 'Ter')
    }
    if (item.wednesday) {
      weekDays ? (weekDays += ' - Qua') : (weekDays += 'Qua')
    }
    if (item.thursday) {
      weekDays ? (weekDays += ' - Qui') : (weekDays += 'Qui')
    }
    if (item.friday) {
      weekDays ? (weekDays += ' - Sex') : (weekDays += 'Sex')
    }
    if (item.saturday) {
      weekDays ? (weekDays += ' - Sáb') : (weekDays += 'Sáb')
    }
    if (item.sunday) {
      weekDays ? (weekDays += ' - Dom') : (weekDays += 'Dom')
    }
    return weekDays
  }

  addNewTeam() {
    this.indexEdit = null
    const dialogRef = this.dialog.open(DepartmentTeamEditComponent, {
      width: '600px',
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.teamList.push(result)
      }
    })
  }

  expand(index: number) {
    this.indexExpanded = this.indexExpanded == index ? -1 : index
  }

  schedule(): void {
    const dialogRef = this.dialog.open(DepartmentScheduleComponent, {
      width: '1200px',
      data: this.departament
    });
  }
}
